import { useEffect, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import {
  DrawingParameterMeasurementLabel,
  DrawingParameterTextAnnotation,
  DrawingStyle,
  DrawingType,
} from '@editor/interfaces/drawing'
import { isDrawingOfType } from '@editor/services/Drawing'
import { getDistanceLabel } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import Konva from 'konva'
import { Layer, Stage, Text } from 'react-konva'

import { DEFAULT_STYLES } from '../../definitions'
import { ExportState, exportDrawings } from '../../store/drawings'
import StackedText from './TextAnnotation'

/**
 * There's some difference in pixel calculation between Konva and Canvas,
 * so we add some extra padding to take care of that.
 * Yes, it's a magic number.
 */
const EXTRA_PADDING = 5

/**
 * Minimum padding to ensure text is not too close to the line.
 */
const MIN_PADDING = 15

const Canvas2D = () => {
  // Store
  const dispatch = useAppDispatch()
  const document2d = useAppSelector((state) => state.blueprint.drawings.document2d)

  // Refs
  const stageRef = useRef<Konva.Stage>(null)

  // States
  const [rect, setRect] = useState({ x: 0, y: 0, width: window.innerWidth, height: window.innerHeight })

  // Derived state
  const mmToPxRatio = document2d.mmToPxRatio || 1
  const extraPaddingScaled = EXTRA_PADDING * document2d.scale
  const minPaddingScaled = MIN_PADDING * document2d.scale

  useEffect(() => {
    if (document2d.exportState !== ExportState.PrepCanvas) return

    const canvas3d = document.querySelector('#canvasBox canvas')
    if (!(canvas3d instanceof HTMLCanvasElement)) return
    setRect({ x: 0, y: 0, width: canvas3d.clientWidth, height: canvas3d.clientHeight })
    dispatch(exportDrawings(ExportState.ProcessData))
  }, [document2d, dispatch])

  return (
    <Box position="absolute" top={0} left={0} style={{ zIndex: 1 }} id="canvas-2d" display="none">
      <Stage ref={stageRef} width={rect.width} height={rect.height}>
        <Layer>
          {document2d.drawings?.map((drawing) => {
            if (isDrawingOfType<DrawingParameterMeasurementLabel>(drawing, DrawingType.MeasurementLabel)) {
              const { uuid, points, styles } = drawing.drawing_parameters
              const offset = styles[DrawingStyle.TextOffset] || 0
              const fontSize =
                (styles[DrawingStyle.TextSize] || DEFAULT_STYLES[DrawingStyle.TextSize] || 18) * document2d.scale

              let padding = 0
              if (points[1][2]) {
                padding = Math.min(
                  -offset * mmToPxRatio - fontSize - extraPaddingScaled,
                  -minPaddingScaled - extraPaddingScaled,
                )
                if (offset < 0) {
                  padding = -offset * mmToPxRatio + extraPaddingScaled
                }
              } else {
                let reversedPadding = 0
                if (offset < 0) {
                  reversedPadding = fontSize + extraPaddingScaled * 2
                }

                padding = offset * mmToPxRatio + extraPaddingScaled - reversedPadding
              }

              return (
                <Text
                  key={uuid}
                  text={getDistanceLabel(points[1][1])}
                  x={points[0][0]}
                  y={points[0][1]}
                  fontSize={fontSize}
                  rotation={points[0][2]}
                  align="center"
                  width={points[1][0]} // width is already scaled
                  padding={padding}
                />
              )
            }

            if (isDrawingOfType<DrawingParameterTextAnnotation>(drawing, DrawingType.TextAnnotation)) {
              const { uuid, transformation, styles, topText, bottomText, width } = drawing.drawing_parameters
              const fontSize = styles[DrawingStyle.TextSize] || DEFAULT_STYLES[DrawingStyle.TextSize] || 18
              return (
                <StackedText
                  key={uuid}
                  topText={topText}
                  bottomText={bottomText}
                  x={transformation[12]}
                  y={transformation[13]}
                  width={width * document2d.scale}
                  fontSize={fontSize * document2d.scale}
                />
              )
            }

            return null
          })}
        </Layer>
      </Stage>
    </Box>
  )
}

export default Canvas2D
